import { Canvas, events as pointerEvents, type RootState } from '@react-three/fiber';
import { type MutableRefObject, type RefObject, useCallback } from 'react';
import type * as THREE from 'three';
import type { HexTileMapKey } from '@/game/hexmap';
import { HexMap, hexMapConfig } from '@/game/hexmap';
import { FOG_COLOR } from '@/game/hexmap/view/tiles/sharedMaterials';
import { baseRuleSet } from '@/game/rules';
import type { Cell } from '@/game/sim';
import type { Frame } from '@/game/view/frame';
import { claimsAKey } from '@/game/view/stance';
import { BuildingsLayer, FENCE_LOOK } from '../BuildingsLayer';
import { CitySprawlLayer } from '../CitySprawlLayer';
import { ConstructionLabels } from '../ConstructionLabels';
import { CameraBridge } from '../EdgeAlerts';
import { FocusMarker } from '../FocusMarker';
import { FogClouds } from '../FogClouds';
import { HealsLayer } from '../HealsLayer';
import { HealthBarsLayer } from '../HealthBarsLayer';
import { HighlightMarkers } from '../HighlightMarkers';
import { HoverHighlight } from '../HoverHighlight';
import { IntentLines } from '../IntentLines';
import { ModelBatcherProvider, prebakeLooks, WATCH_SPOT_LOOK } from '../ModelBatcher';
import { AttackTargetMarkers, MoveTargetMarker } from '../MoveTargetMarker';
import { OceanSurround } from '../OceanSurround';
import { PerfProbe } from '../PerfOverlay';
import { PlacementOverlay } from '../PlacementOverlay';
import { PERF_ENABLED } from '../perf';
import type { Pickable } from '../picking';
import { AutoQualityMonitor, FrameLimiter, type useQuality } from '../QualitySettings';
import { RivalCityMarkers } from '../RivalCityMarkers';
import { ShotsLayer } from '../ShotsLayer';
import { StrategyCamera, type StrategyCameraHandle } from '../StrategyCamera';
import { TerritoryLayer } from '../TerritoryLayer';
import { MatchTileDecorations } from '../TileDecorations';
import { UnitsLayer } from '../UnitsLayer';
import { DRAG_PX } from '../ui/boxSelect';
import { type UiStore, useUi } from '../ui/store';
import { WatchSpotsLayer } from '../WatchSpotsLayer';
import { useFog, useFrameStore, useSimSelector } from './simFrame';
import type { MatchModes } from './useMatchModes';
import type { usePointerOrders } from './usePointerOrders';
import type { Selection } from './useSelection';

export const TILE_THICKNESS = hexMapConfig.hexMap.tile.thickness;
/** Decorative water drawn beyond the map edge, this many hexes further out. */
const OCEAN_MARGIN = 34;
/** Same as the fog tiles, so the unexplored world fades into it with no edge. */
const BACKGROUND = FOG_COLOR;
/** Every model look the rules can show, baked while the page is idle. */
const PREBAKE_LOOKS = prebakeLooks(baseRuleSet, [FENCE_LOOK, WATCH_SPOT_LOOK]);
const CAMERA = { fov: 32, near: 0.1, far: 500 };
const GL_AA = { antialias: true };
const GL_NO_AA = { antialias: false };
const terrainOf = (frame: Frame) => frame.terrain;

/**
 * R3F's pointer events, raycast from the pointer's client position against
 * the canvas rect rather than `offsetX/Y`: those are measured from the event's
 * target, which for an in-world `Html` element (rendered into the same
 * wrapper R3F listens on) is that element, not the canvas, so a move over a
 * fog city marker would otherwise hover the tile at the canvas's top-left.
 */
function clientPointerEvents(store: Parameters<typeof pointerEvents>[0]) {
  return {
    ...pointerEvents(store),
    compute(event: MouseEvent, state: RootState) {
      const rect = state.gl.domElement.getBoundingClientRect();
      state.pointer.set(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
      );
      state.raycaster.setFromCamera(state.pointer, state.camera);
    },
  };
}

/**
 * The 3D view: map, ocean and clouds, tints for the armed mode, tile props,
 * every unit and building (instanced, one batcher), markers, combat effects
 * and labels, and the strategy camera. Each layer reads the frame itself.
 */
export function MatchScene({
  ui,
  quality,
  mapRadius,
  modes,
  selection,
  pointer,
  attackRings,
  setCamera,
  viewCameraRef,
  onSelectUnit,
  drawnRef,
}: {
  ui: UiStore;
  quality: ReturnType<typeof useQuality>;
  mapRadius: number;
  modes: MatchModes;
  selection: Selection;
  pointer: ReturnType<typeof usePointerOrders>;
  /** Send troops targets, ringed until each attack resolves. */
  attackRings: readonly Cell[];
  setCamera: (controls: StrategyCameraHandle | null) => void;
  viewCameraRef: RefObject<THREE.Camera | null>;
  /** A left-click on an own unit (`useBoxSelect` wraps the selection's to add double-click). */
  onSelectUnit: (id: string, additive: boolean) => void;
  /** Filled with where units are drawn, for box-select. */
  drawnRef: MutableRefObject<readonly Pickable[]>;
}) {
  const { profile } = quality;
  const frames = useFrameStore();
  // A is attack-move, not pan-left, while fighters are selected or targeting.
  const isKeyClaimed = useCallback(
    (key: string) => {
      if (key !== 'a') return false;
      const { view, rules, playerId } = frames.get();
      const { selectedUnitIds, targetMode } = ui.get();
      return claimsAKey(view, rules, playerId, selectedUnitIds, targetMode !== null);
    },
    [frames, ui]
  );
  const fog = useFog();
  const terrain = useSimSelector(terrainOf);
  const { placementMode, targetMode, placeable, watchable, foundable, targetable } = modes;
  const selectedUnitIds = useUi(ui, (s) => s.selectedUnitIds);
  const selectedBuildingId = useUi(ui, (s) => s.selectedBuildingId);
  // Clicks go to the tiles while a mode is armed.
  const picking = placementMode !== null || targetMode !== null;
  /** Pointy-top hex rows are √3·radius apart, so this is the map's world radius. */
  const cameraBounds = mapRadius * Math.sqrt(3) * hexMapConfig.hexMap.tile.radius;
  const hoverAllowed: ReadonlySet<HexTileMapKey> | null =
    placeable ?? (targetMode ? targetable : null);

  return (
    <Canvas
      // Antialiasing is fixed per WebGL context, and R3F zeroes its clock when
      // `frameloop` changes (leaving layers' clock stamps in the future):
      // either change remounts the canvas, so the clock and layers start fresh.
      key={`${profile.antialias ? 'aa' : 'no-aa'}-${profile.fpsCap ? 'capped' : 'free'}`}
      camera={CAMERA}
      events={clientPointerEvents}
      dpr={profile.dpr}
      gl={profile.antialias ? GL_AA : GL_NO_AA}
      frameloop={profile.fpsCap ? 'never' : 'always'}
    >
      {profile.fpsCap ? <FrameLimiter fps={profile.fpsCap} /> : null}
      {PERF_ENABLED ? <PerfProbe /> : null}
      {quality.setting === 'auto' && !quality.autoLow ? (
        <AutoQualityMonitor onDecline={quality.onAutoDecline} />
      ) : null}
      <color attach="background" args={[BACKGROUND]} />
      <ambientLight intensity={0.45} />
      <directionalLight position={[10, 18, 8]} intensity={1.1} />
      {/* Hover tracking on the board only; units/buildings sit above and
          don't intercept, so the hovered tile is the one under the cursor. */}
      <group onPointerMove={pointer.onBoardPointerMove} onPointerOut={pointer.onBoardPointerOut}>
        <HexMap
          tiles={terrain}
          onTilePointerDown={pointer.onTilePointerDown}
          getTileVisibility={fog.sight}
        />
      </group>
      <OceanSurround
        mapRadius={mapRadius}
        oceanRadius={mapRadius + OCEAN_MARGIN}
        backgroundColor={BACKGROUND}
        getTileVisibility={fog.sight}
      />
      <FogClouds mapRadius={mapRadius} puffsPerTile={profile.fogPuffsPerTile} />
      <HoverHighlight ui={ui} allowed={hoverAllowed} />
      {placeable && <PlacementOverlay tiles={placeable} />}
      {targetMode?.kind === 'watch' && watchable && (
        <PlacementOverlay tiles={watchable} tone="accent" />
      )}
      {foundable && <PlacementOverlay tiles={foundable} tone="accent" />}
      <MatchTileDecorations density={profile.decorationDensity} />
      {/* Units, buildings, sprawl and watch spots: instanced, one batcher. */}
      <ModelBatcherProvider prebake={PREBAKE_LOOKS}>
        <CitySprawlLayer />
        <TerritoryLayer />
        <BuildingsLayer
          selectedBuildingId={selectedBuildingId}
          onSelectBuilding={picking ? undefined : selection.selectBuilding}
        />
        <UnitsLayer
          selectedUnitIds={selectedUnitIds}
          onSelectUnit={picking ? undefined : onSelectUnit}
          drawnRef={drawnRef}
          ui={ui}
          onUnitPointerDown={pointer.onUnitPointerDown}
        />
        <MoveTargetMarker selectedUnitIds={selectedUnitIds} ui={ui} />
        <IntentLines selectedUnitIds={selectedUnitIds} />
        <WatchSpotsLayer selectedBuildingId={selectedBuildingId} />
      </ModelBatcherProvider>
      <AttackTargetMarkers cells={attackRings} />
      <HighlightMarkers ui={ui} />
      <FocusMarker selectedUnitIds={selectedUnitIds} />
      <ShotsLayer />
      <HealthBarsLayer />
      <HealsLayer />
      <RivalCityMarkers onPointerDown={pointer.onMarkerPointerDown} />
      <ConstructionLabels />
      <CameraBridge cameraRef={viewCameraRef} />
      <StrategyCamera
        ref={setCamera}
        groundY={TILE_THICKNESS}
        boundsRadius={cameraBounds}
        dragThreshold={DRAG_PX}
        isKeyClaimed={isKeyClaimed}
      />
    </Canvas>
  );
}
